import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (query: string) => void;
}

export default function ImageSearchModal({ isOpen, onClose, onSearch }: Props) {
  const { lang, allProducts } = useApp();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setImagePreview(null);
      setIsScanning(false);
    }
  }, [isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImagePreview(url);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = reader.result?.toString().split(',')[1];
        if (base64Data) {
          startScanning(base64Data);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const startScanning = async (base64Data: string) => {
    setIsScanning(true);
    
    try {
      const response = await fetch('/api/gemini/vision-search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64Data })
      });
      
      if (!response.ok) {
        throw new Error('Failed to analyze image');
      }
      
      const data = await response.json();
      
      if (data.keyword && data.keyword !== 'Not Found') {
        onSearch(data.keyword);
      } else {
        // Fallback or show error
        onSearch('');
        alert(lang === 'en' ? 'Could not find any beauty products in this image.' : 'এই ছবিতে কোনো বিউটি প্রোডাক্ট পাওয়া যায়নি।');
      }
    } catch (error) {
      console.error(error);
      alert(lang === 'en' ? 'An error occurred during analysis.' : 'ছবি বিশ্লেষণ করার সময় একটি সমস্যা হয়েছে।');
    } finally {
      setIsScanning(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes scan-line {
          0% { top: 0%; }
          50% { top: 98%; }
          100% { top: 0%; }
        }
      `}</style>
      <div className="fixed inset-0 z-[99999] bg-black/90 flex flex-col items-center justify-center p-4 animate-fade-in">
        <div className="bg-white dark:bg-[#121215] w-full max-w-md rounded-2xl overflow-hidden relative shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-neutral-100 dark:border-neutral-800">
            <h3 className="font-bold text-neutral-800 dark:text-neutral-200">
              {lang === 'en' ? 'Visual Search' : 'ছবি দিয়ে খুঁজুন'}
            </h3>
            <button 
              onClick={onClose} 
              className="p-2 text-neutral-500 hover:text-primary bg-neutral-100 dark:bg-neutral-800 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-6 flex flex-col items-center justify-center min-h-[300px] bg-neutral-50 dark:bg-neutral-900/50">
            {imagePreview ? (
              <div className="relative w-full max-w-[250px] aspect-square rounded-xl overflow-hidden shadow-lg border border-neutral-200 dark:border-neutral-800">
                <img src={imagePreview} alt="Search" className="w-full h-full object-cover" />
                {isScanning && (
                  <>
                    <div className="absolute inset-0 bg-primary/10 animate-pulse"></div>
                    <div 
                      className="absolute left-0 right-0 h-1 bg-primary shadow-[0_0_15px_rgba(var(--color-primary-rgb),0.8)]" 
                      style={{ animation: 'scan-line 2s ease-in-out infinite' }}
                    ></div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 backdrop-blur-[2px]">
                       <Loader2 className="w-8 h-8 text-white animate-spin mb-2" />
                       <span className="text-white font-medium text-sm">
                         {lang === 'en' ? 'Analyzing Image...' : 'ছবি বিশ্লেষণ করা হচ্ছে...'}
                       </span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="w-full grid grid-cols-2 gap-4">
                <button 
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-3 p-6 bg-white dark:bg-[#1A1A1E] rounded-xl border border-neutral-200 dark:border-neutral-800 hover:border-primary dark:hover:border-primary transition-colors shadow-sm"
                >
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <Camera className="w-6 h-6" />
                  </div>
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {lang === 'en' ? 'Take Photo' : 'ছবি তুলুন'}
                  </span>
                </button>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-3 p-6 bg-white dark:bg-[#1A1A1E] rounded-xl border border-neutral-200 dark:border-neutral-800 hover:border-primary dark:hover:border-primary transition-colors shadow-sm"
                >
                  <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {lang === 'en' ? 'Upload Image' : 'ছবি আপলোড'}
                  </span>
                </button>
                <input 
                  type="file" 
                  accept="image/*" 
                  capture="environment"
                  className="hidden" 
                  ref={cameraInputRef} 
                  onChange={handleFileChange} 
                />
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                />
              </div>
            )}
          </div>
          
          {!imagePreview && (
            <div className="p-4 text-center text-[13px] font-medium text-neutral-500 bg-white dark:bg-[#121215]">
              {lang === 'en' 
                ? 'Upload or take a photo of a product to find similar items' 
                : 'একই ধরনের পণ্য খুঁজতে ছবি তুলুন বা আপলোড করুন'}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
